<script setup lang="ts">
import { BaseButton, BaseInputSelect, BaseModal, BaseToastEditorContent } from '@template/ui';
import { computed, watch } from 'vue';

export interface TermsOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Props {
  isOpen: boolean;
  title?: string;
  /**
   * 약관 선택 리스트
   * - 0개: 선택 UI 미표시
   * - 1개: 선택 UI 비활성화
   */
  termsOptions?: TermsOption[];
  /**
   * 약관 본문
   * - string: 단일 컨텐츠
   * - Record: termsOptions.value를 키로 하는 컨텐츠 맵
   */
  content: string | Record<string, string>;
  /**
   * 컨텐츠 내 span 태그를 제거할지 여부
   * - 기본 true: escape/변환 과정에서 span 태그가 깨질 때 안전
   * - false: span 스타일을 그대로 유지해야 하는 컨텐츠(예: 개인정보 표)에서 사용
   */
  stripSpanTags?: boolean;
  /**
   * 하단 버튼 개수
   * - undefined: 버튼 영역 미표시
   * - 1: 닫기 1버튼(기본)
   * - 2: 닫기 + 동의함 2버튼(기본)
   */
  footerButtons?: 1 | 2;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void;
  (e: 'agree', value: string): void;
  (e: 'close'): void;
}>();

// TermsDialog와 동일하게 `v-model:selected-tab` 패턴을 지원합니다.
const selectedTab = defineModel<string>('selectedTab', {});

const options = computed(() => props.termsOptions ?? []);
const footerButtons = computed(() => props.footerButtons);

const selectOptions = computed(() =>
  options.value.map((o) => ({
    value: o.value,
    label: o.label,
    disabled: o.disabled,
  }))
);

const normalizePolicyContent = (raw: string): string => {
  // BaseToastEditorContent가 일부 escape/HTML 문자열로 들어올 때를 대비합니다.
  let next = raw
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/<span[^>]*>/gi, '')
    .replace(/<\/span>/gi, '');

  // span 태그가 의도된 컨텐츠라면 보존합니다.
  if (props.stripSpanTags === false) {
    next = raw.replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }

  return next;
};

const ensureSelectedTab = () => {
  const current = selectedTab.value;
  const list = options.value;
  if (list.length === 0) {
    if (current) selectedTab.value = '';
    return;
  }
  if (!current || !list.some((o) => o.value === current)) {
    selectedTab.value = list[0].value;
  }
};

watch(
  () => [props.isOpen, options.value],
  () => {
    if (!props.isOpen) return;
    ensureSelectedTab();
  },
  { immediate: true, deep: true }
);

const resolvedContent = computed(() => {
  const rawContent = props.content;
  if (typeof rawContent === 'string') {
    return normalizePolicyContent(rawContent);
  }

  const list = options.value;
  if (list.length === 0) {
    const firstKey = Object.keys(rawContent)[0];
    return firstKey ? normalizePolicyContent(rawContent[firstKey]) : '';
  }

  const key = selectedTab.value || list[0].value;
  return rawContent[key] ? normalizePolicyContent(rawContent[key]) : '';
});

const selectedValueForAgree = computed(() => {
  const list = options.value;
  if (list.length === 0) return selectedTab.value || '';
  return selectedTab.value || list[0].value;
});

const handleClose = () => {
  emit('close');
  emit('update:isOpen', false);
};

const handleAgree = () => {
  emit('agree', selectedValueForAgree.value);
  emit('update:isOpen', false);
};
</script>

<template>
  <BaseModal
    :is-open="props.isOpen"
    :title="props.title ?? '약관 상세보기'"
    :show-default-footer="false"
    :close-on-overlay-click="true"
    :close-on-escape="true"
    content-padding="none"
    @close="handleClose"
    @update:is-open="$emit('update:isOpen', $event)"
  >
    <div class="flex max-h-[80vh] min-h-[600px] flex-col">
      <!-- 약관 선택 UI -->
      <div v-if="options.length > 0" class="px-6 pt-6">
        <div class="w-[320px]">
          <BaseInputSelect
            v-model="selectedTab"
            :options="selectOptions"
            :disabled="options.length === 1"
            class="w-full"
            size="md"
            :teleport="false"
          />
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <BaseToastEditorContent :modelValue="resolvedContent" />
      </div>

      <div
        v-if="footerButtons"
        class="flex gap-3 py-4"
        :style="{
          paddingInline: 'var(--padding-padding-36)',
          paddingBottom: '40px',
          backgroundColor: 'var(--popup-background)',
        }"
      >
        <BaseButton
          label="닫기"
          variant="outlined"
          color="grey"
          size="sm"
          full-width
          @click="handleClose"
        />
        <BaseButton
          v-if="footerButtons === 2"
          label="동의함"
          variant="contained"
          color="primary"
          size="sm"
          full-width
          @click="handleAgree"
        />
      </div>
    </div>
  </BaseModal>
</template>
